import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, ShieldCheck, Award, AlertTriangle, Building2, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const Footer = () => {
  const { isRTL } = useLanguage();

  const sections = [
    {
      title: isRTL ? 'الأسواق' : 'Markets',
      links: [
        { label: isRTL ? 'الفوركس' : 'Forex', href: '/markets/forex' },
        { label: isRTL ? 'المؤشرات' : 'Indices', href: '/markets/indices' },
        { label: isRTL ? 'السلع' : 'Commodities', href: '/markets/commodities' },
        { label: isRTL ? 'الأسهم' : 'Shares', href: '/markets/shares' },
        { label: 'ETFs', href: '/markets/etfs' },
        { label: isRTL ? 'السندات' : 'Bonds', href: '/markets/bonds' },
      ],
    },
    {
      title: isRTL ? 'الحسابات' : 'Accounts',
      links: [
        { label: 'Pro ECN', href: '/accounts/pro-ecn' },
        { label: 'Raw ECN', href: '/accounts/raw-ecn' },
        { label: 'Standard STP', href: '/accounts/standard' },
        { label: 'Swap-Free', href: '/accounts/swap-free' },
        { label: isRTL ? 'حاسبة التداول' : 'Calculator', href: '/fees/calculator' },
      ],
    },
    {
      title: isRTL ? 'المنصّات' : 'Platforms',
      links: [
        { label: isRTL ? 'تطبيق VAROTRADES' : 'VAROTRADES App', href: '/platforms/app' },
        { label: 'TradingView', href: '/platforms/tradingview' },
        { label: isRTL ? 'منصة الويب' : 'Web Platform', href: '/platforms/web' },
      ],
    },
    {
      title: isRTL ? 'الموارد' : 'Resources',
      links: [
        { label: isRTL ? 'الأكاديمية' : 'Academy', href: '/learn' },
        { label: isRTL ? 'الندوات' : 'Webinars', href: '/learn/webinars' },
        { label: isRTL ? 'تقويم اقتصادي' : 'Economic Calendar', href: '/analysis/calendar' },
        { label: isRTL ? 'تحليل السوق' : 'Market News', href: '/analysis/news' },
        { label: isRTL ? 'الأكاديمية' : 'Glossary', href: '/learn/glossary' },
      ],
    },
    {
      title: isRTL ? 'الشركة' : 'Company',
      links: [
        { label: isRTL ? 'عن VAROTRADES' : 'About', href: '/about' },
        { label: isRTL ? 'الترخيص' : 'Regulation', href: '/about/regulation' },
        { label: isRTL ? 'تأمين الأموال' : 'Fund Insurance', href: '/about/insurance' },
        { label: isRTL ? 'الجوائز' : 'Awards', href: '/about/awards' },
        { label: isRTL ? 'تواصل' : 'Contact', href: '/about/contact' },
      ],
    },
  ];

  const socials = [];

  return (
    <footer className="relative bg-white border-t border-[rgba(20,15,10,0.06)]" data-testid="site-footer" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Risk Warning Bar (at top) */}
      <div className="bg-flame-50 border-b border-flame-100">
        <div className={`max-w-[1440px] mx-auto px-6 lg:px-10 py-4 flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
          <AlertTriangle className="w-5 h-5 text-flame-600 shrink-0 mt-0.5" />
          <p className="text-[12px] leading-relaxed text-ink-700">
            <strong className="text-flame-700">{isRTL ? 'تحذير المخاطر:' : 'Risk Warning:'}</strong>{' '}
            {isRTL
              ? 'تداول العقود مقابل الفروقات (CFDs) ينطوي على درجة عالية من المخاطرة وقد يؤدي إلى خسارة كل رأس مالك. حوالي 76% من حسابات المتداولين الأفراد تخسر المال عند تداول CFDs مع هذا المزوّد. يجب عليك التأكّد من فهمك الكامل لكيفية عمل CFDs وما إذا كنت تستطيع تحمّل المخاطر العالية لخسارة أموالك. لا تستثمر إلا بالأموال التي يمكنك تحمّل خسارتها.'
              : 'Trading Contracts for Difference (CFDs) involves a high degree of risk and may result in the loss of all of your capital. Approximately 76% of retail trader accounts lose money when trading CFDs with this provider. You should ensure you fully understand how CFDs work and whether you can afford the high risk of losing your money. Only invest funds you can afford to lose.'}
          </p>
        </div>
      </div>

      {/* Main grid */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
        <div className="col-span-2 lg:col-span-2">
          <Link to="/" className="inline-flex items-center">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/varotrade_official_emblem.png`} alt="VAROTRADES" className="h-9 w-auto" />
          </Link>
          <p className="mt-5 text-ink-600 text-[13px] leading-relaxed">
            {isRTL
              ? 'VAROTRADE SERVICES LTD هي وسيط دولي مرخّص ومنظّم، متخصّص في تنفيذ عقود الفروقات (CFDs) عبر الفوركس، المؤشرات، السلع، الأسهم، ETFs، والسندات. مرخّصة من لجنة الخدمات المالية الموريشيوسية (FSC) — Securities Dealer License GB21026396 · Company Reg. C179725.'
              : 'VAROTRADE SERVICES LTD is a licensed and regulated international broker specialising in CFD execution across Forex, Indices, Commodities, Shares, ETFs, and Bonds. Licensed by the Financial Services Commission of Mauritius (FSC) — Securities Dealer License GB21026396 · Company Reg. C179725.'}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {['FSC Mauritius', 'GB21026396', 'C179725', 'FATF · OECD'].map((r) => (
              <span key={r} className="text-[10px] font-bold tracking-wider px-2 py-1 rounded bg-flame-50 border border-flame-100 text-flame-700 font-mono">{r}</span>
            ))}
          </div>
          <a
            href="https://opr.fscmauritius.org/ords/opr/r/fsc-opr/fsc-online-public-register-opr"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-verify-license-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-flame-500 hover:bg-flame-600 text-white text-xs font-black transition-all shadow-sm hover:shadow-md"
          >
            <ShieldCheck className="w-4 h-4" />
            {isRTL ? 'تحقّق من الترخيص على موقع FSC الرسمي' : 'Verify license on official FSC site'}
            <ExternalLink className="w-3 h-3 opacity-80" />
          </a>
          {socials.length > 0 && (
            <div className={`mt-5 flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {socials.map(({ Icon, href, label }) => (
                <a key={label} href={href} aria-label={label} className="w-9 h-9 rounded-xl bg-flame-50 hover:bg-flame-100 border border-flame-100 hover:border-flame-300 flex items-center justify-center text-flame-600 transition-all" data-testid={`social-${label.toLowerCase()}`}>
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          )}
        </div>

        {sections.map((section, i) => (
          <div key={i}>
            <p className="varo-eyebrow text-ink-500 mb-4 text-[10px]">{section.title}</p>
            <ul className="space-y-2.5">
              {section.links.map((l, j) => (
                <li key={j}>
                  <Link to={l.href} className="text-[12.5px] text-ink-700 hover:text-flame-600 transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Long-form regulatory & risk disclosure */}
      <div className="border-t border-[rgba(20,15,10,0.05)] bg-[#FAF7F2]">
        <div className={`max-w-[1440px] mx-auto px-6 lg:px-10 py-12 space-y-6 text-[12px] text-ink-600 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
          {/* About paragraph */}
          <div>
            <h4 className="varo-display text-base text-ink-900 mb-2">{isRTL ? 'عن VAROTRADES' : 'About VAROTRADES'}</h4>
            <p>
              {isRTL
                ? 'VAROTRADE SERVICES LTD هي شركة وساطة عالمية تأسّست في عام 2021، متخصّصة في تقديم خدمات تنفيذ التداول للعقود مقابل الفروقات (CFDs) عبر مجموعة واسعة من الأسواق المالية تشمل الفوركس، المؤشرات الأمريكية والأوروبية والآسيوية، السلع (الذهب، النفط، الفضة)، الأسهم العالمية (1,000+ سهم)، صناديق الاستثمار المتداولة (ETFs)، والسندات السيادية. تخدم الشركة أكثر من 25,000 متداول حول العالم في 50+ دولة، وتعتمد على بنية تحتية مؤسسية موزّعة عبر مراكز بيانات Equinix العالمية لتوفير تنفيذ بزمن أقل من 30 ميلي ثانية.'
                : 'VAROTRADE SERVICES LTD is a global brokerage founded in 2021, specialising in execution services for Contracts for Difference (CFDs) across a wide range of financial markets including Forex, US/European/Asian indices, commodities (gold, oil, silver), global shares (1,000+), exchange-traded funds (ETFs), and sovereign bonds. The company serves over 25,000 traders across 50+ countries, leveraging an institutional infrastructure across Equinix data centres to deliver sub-30-millisecond execution.'}
            </p>
          </div>

          {/* License paragraph */}
          <div>
            <h4 className="varo-display text-base text-ink-900 mb-2">{isRTL ? 'المعلومات التنظيمية' : 'Regulatory Information'}</h4>
            <p>
              <strong>VAROTRADE SERVICES LTD</strong>{' '}
              {isRTL
                ? 'مرخّصة ومنظّمة من قبل لجنة الخدمات المالية الموريشيوسية (Financial Services Commission of Mauritius — FSC) كـ "Securities Dealer (Full Service Dealer)" تحت رقم الترخيص الرسمي GB21026396، ورقم تسجيل الشركة C179725. عنوان الشركة المسجّل: 6th Floor, Tower 1, Nexteracom Building, Ebene, Republic of Mauritius. لتوثيق الترخيص، يمكن للعملاء زيارة الموقع الرسمي للجنة على'
                : 'is licensed and regulated by the Financial Services Commission of Mauritius (FSC) as a "Securities Dealer (Full Service Dealer)" under official license number GB21026396 (Company Registration No. C179725). Registered office: 6th Floor, Tower 1, Nexteracom Building, Ebene, Republic of Mauritius. To verify the license, clients may visit the FSC\'s official website at'}{' '}
              <a href="https://opr.fscmauritius.org/ords/opr/r/fsc-opr/fsc-online-public-register-opr" target="_blank" rel="noopener noreferrer" className="text-flame-600 hover:underline font-bold">FSC Online Public Register</a>
              {' '}{isRTL ? 'والبحث في قسم "Licensee Search" باسم الشركة أو رقم الترخيص.' : 'and search the "Licensee Search" section by company name or license number.'}
            </p>
          </div>

          {/* CFD Risk Disclosure */}
          <div>
            <h4 className="varo-display text-base text-ink-900 mb-2">{isRTL ? 'الإفصاح عن مخاطر CFDs' : 'CFD Risk Disclosure'}</h4>
            <p>
              {isRTL
                ? 'العقود مقابل الفروقات (CFDs) هي منتجات مالية معقّدة ذات رافعة مالية تنطوي على درجة عالية من المخاطر. عند تداول CFDs، فأنت لا تمتلك الأصل الأساسي بل تتعاقد على فروقات سعره. الرافعة المالية يمكن أن تعمل ضدّك بقدر ما تعمل لصالحك — قد تخسر كل رأس مالك بسرعة، وأكثر من ذلك في حال انعدام حماية الرصيد السلبي. حوالي 76% من حسابات المتداولين الأفراد تخسر المال عند تداول CFDs مع وسطاء معتمدين دولياً. قبل اتخاذ أي قرار للتداول، يجب عليك التفكير بعناية في أهدافك الاستثمارية، مستوى خبرتك، ومدى تحمّلك للمخاطر. لا تتداول إلا بالأموال التي يمكنك تحمّل خسارتها كلياً، واستشر مستشاراً مالياً مستقلاً إذا كانت لديك أي شكوك.'
                : 'Contracts for Difference (CFDs) are complex, leveraged financial products that carry a high degree of risk. When trading CFDs, you do not own the underlying asset — you contract on its price differential. Leverage can work against you as much as it works for you — you may lose all your capital quickly, and more in the absence of negative balance protection. Approximately 76% of retail trader accounts lose money when trading CFDs with internationally regulated brokers. Before any decision to trade, you should carefully consider your investment objectives, level of experience, and risk appetite. Only trade with funds you can afford to lose entirely, and consult an independent financial advisor if you have any doubts.'}
            </p>
          </div>

          {/* Geographic Restrictions */}
          <div>
            <h4 className="varo-display text-base text-ink-900 mb-2">{isRTL ? 'القيود الجغرافية' : 'Geographic Restrictions'}</h4>
            <p>
              {isRTL
                ? 'لا تقدّم VAROTRADE SERVICES LTD خدماتها لمقيمي الولايات المتحدة الأمريكية، إيران، كوريا الشمالية، سوريا، كوبا، السودان، والمناطق الأخرى الخاضعة لعقوبات OFAC أو الأمم المتحدة. المعلومات الواردة في هذا الموقع ليست موجّهة ولا مخصّصة للتوزيع أو الاستخدام من قبل أي شخص أو كيان في أي ولاية قضائية يكون فيها هذا التوزيع أو الاستخدام مخالفاً للقانون أو اللوائح المحلية.'
                : 'VAROTRADE SERVICES LTD does not offer its services to residents of the United States, Iran, North Korea, Syria, Cuba, Sudan, and other regions subject to OFAC or UN sanctions. The information on this website is not directed at, nor intended for distribution to or use by, any person or entity in any jurisdiction where such distribution or use would be contrary to local law or regulation.'}
            </p>
          </div>

          {/* No advice */}
          <div>
            <h4 className="varo-display text-base text-ink-900 mb-2">{isRTL ? 'إخلاء مسؤولية' : 'Disclaimer'}</h4>
            <p>
              {isRTL
                ? 'المعلومات والآراء والتحليلات والأخبار المنشورة على هذا الموقع لا تشكّل توصية استثمارية أو نصيحة مالية. كل البيانات المعروضة لأغراض إعلامية فقط. الأداء السابق ليس مؤشّراً على الأداء المستقبلي. VAROTRADE SERVICES LTD لا تتحمّل أي مسؤولية عن أي خسارة قد تنشأ نتيجة الاعتماد على المعلومات الواردة في هذا الموقع. التداول قرار شخصي ومسؤولية كاملة على المتداول.'
                : 'Information, opinions, analyses, and news published on this website do not constitute investment recommendations or financial advice. All data is presented for informational purposes only. Past performance is not indicative of future performance. VAROTRADE SERVICES LTD accepts no liability for any loss that may arise from reliance on information on this website. Trading is a personal decision and the trader\'s sole responsibility.'}
            </p>
          </div>

          {/* Compliance */}
          <div className={`flex flex-wrap gap-4 pt-4 border-t border-flame-100 text-[11px] ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-flame-500" />{isRTL ? 'Ebene, Mauritius · Reg. C179725' : 'Ebene, Mauritius · Reg. C179725'}</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-flame-500" />FSC GB21026396</span>
            <span className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-flame-500" />KPMG Audited</span>
            <span className="inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-flame-500" />50+ {isRTL ? 'دولة' : 'countries'}</span>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[rgba(20,15,10,0.05)] bg-white">
        <div className={`max-w-[1440px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-ink-500 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
          <p>© {new Date().getFullYear()} VAROTRADE SERVICES LTD. {isRTL ? 'جميع الحقوق محفوظة.' : 'All rights reserved.'}</p>
          <div className={`flex flex-wrap items-center gap-x-5 gap-y-2`}>
            <Link to="/about/legal" className="hover:text-flame-600 transition-colors">{isRTL ? 'الشروط' : 'Terms'}</Link>
            <Link to="/legal/privacy" className="hover:text-flame-600 transition-colors">{isRTL ? 'الخصوصية' : 'Privacy'}</Link>
            <Link to="/legal/aml" className="hover:text-flame-600 transition-colors">AML</Link>
            <Link to="/legal/cookies" className="hover:text-flame-600 transition-colors">{isRTL ? 'الكوكيز' : 'Cookies'}</Link>
            <Link to="/about/regulation" className="hover:text-flame-600 transition-colors font-bold text-flame-600">{isRTL ? 'الترخيص' : 'License'}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
